/**
 * XY-Chain -- the Evil tier.
 *
 * A run of bivalue cells, each seeing the next and sharing a digit with it.
 * Push a "not Z" assumption in at one end and it propagates all the way down
 * the chain: if the first cell is not Z it must be its other digit, which
 * forces the second, and so on until the last cell is forced to Z. So Z sits at
 * one end of the chain or the other -- and any cell that can see both ends
 * cannot have it.
 *
 * Implemented as a breadth-first search over states of the form "cell C holds
 * digit D". Bivalue cells make each state force exactly one successor per peer,
 * so the search space is bounded by the 729 possible states and stays fast;
 * BFS also means the chain reported to the player is the shortest one, which
 * matters when the point is to teach it.
 */

import { CELLS, EMPTY, cellName, type Digit } from "../grid";
import { digitsOf, maskOf, popcount } from "../candidates";
import { peerMatrix } from "../constraints";
import type { Context, Step, Technique } from "./types";

interface State {
  readonly cell: number;
  readonly digit: Digit;
}

export const xyChain: Technique = {
  id: "xy-chain",
  name: "XY-Chain",
  tier: "evil",
  ser: 6.6,
  find({ board, candidates, cs }: Context): Step | null {
    const matrix = peerMatrix(cs);

    const bivalue: number[] = [];
    for (let cell = 0; cell < CELLS; cell++) {
      if (board[cell] === EMPTY && popcount(candidates[cell]!) === 2) bivalue.push(cell);
    }
    if (bivalue.length < 3) return null;

    const key = (cell: number, digit: Digit) => cell * 9 + (digit - 1);

    for (const start of bivalue) {
      for (const target of digitsOf(candidates[start]!)) {
        // Assume the start is NOT `target`, so it is its other digit.
        const other = digitsOf(candidates[start]!).find((d) => d !== target)!;

        const parent = new Map<number, State | null>();
        parent.set(key(start, other), null);
        let queue: State[] = [{ cell: start, digit: other }];

        while (queue.length > 0) {
          const nextQueue: State[] = [];
          for (const state of queue) {
            const bit = maskOf(state.digit);
            for (const peer of bivalue) {
              if (peer === state.cell) continue;
              if (matrix[state.cell * CELLS + peer] !== 1) continue;
              if ((candidates[peer]! & bit) === 0) continue;

              // The peer cannot be `state.digit`, so it takes its other value.
              const forced = digitsOf(candidates[peer]!).find((d) => d !== state.digit)!;
              const id = key(peer, forced);
              if (parent.has(id)) continue;
              parent.set(id, state);

              if (forced === target && peer !== start) {
                // Rebuild the chain, start-first.
                const chain: State[] = [];
                let cursor: State | null = { cell: peer, digit: forced };
                while (cursor) {
                  chain.unshift(cursor);
                  cursor = parent.get(key(cursor.cell, cursor.digit)) ?? null;
                }
                if (chain.length < 3) continue;

                const members = new Set(chain.map((s) => s.cell));
                const targetBit = maskOf(target);
                const eliminations = [];
                for (let cell = 0; cell < CELLS; cell++) {
                  if (board[cell] !== EMPTY || members.has(cell)) continue;
                  if ((candidates[cell]! & targetBit) === 0) continue;
                  if (matrix[start * CELLS + cell] === 1 && matrix[peer * CELLS + cell] === 1) {
                    eliminations.push({ cell, digit: target });
                  }
                }
                if (eliminations.length === 0) continue;

                return {
                  technique: "xy-chain",
                  pattern: chain.map((s) => s.cell),
                  digits: [target],
                  placements: [],
                  eliminations,
                  explanation:
                    `XY-Chain: follow ${chain.map((s) => cellName(s.cell)).join(" -> ")}. ` +
                    `If ${cellName(start)} is not ${target} it must be ${other}, and that ` +
                    `forces each cell along the chain until ${cellName(peer)} is ${target}. ` +
                    `So ${target} is in ${cellName(start)} or ${cellName(peer)}, and can be ` +
                    `removed from any cell seeing both: ` +
                    `${eliminations.map((e) => cellName(e.cell)).join(", ")}.`,
                };
              }

              nextQueue.push({ cell: peer, digit: forced });
            }
          }
          queue = nextQueue;
        }
      }
    }
    return null;
  },
};
